import React from 'react';
import { mergeStyleSets, Icon, Rating } from '@fluentui/react';
import htmr from 'htmr';

const CORRECT = '#2E9E4F';
const INCORRECT = '#D93025';
const CORRECT_BG = 'rgba(46,158,79,0.10)';
const INCORRECT_BG = 'rgba(217,48,37,0.08)';
const SELECTED_BG = 'rgba(60,60,67,0.08)';

const ratingTypeOptions = [
    { key: 'StarburstSolid', icon: 'StarburstSolid', unselectedIcon: 'Starburst' },
    { key: 'RadioBtnOff', icon: 'RadioBtnOn', unselectedIcon: 'RadioBtnOff' },
    { key: 'FavoriteStar', icon: 'FavoriteStarFill', unselectedIcon: 'FavoriteStar' },
];

// Read-only review of an already-answered step.
// props: data (step), review ({ selectedIds, value, correct|null })
function ReviewStep(props) {
    const step = props.data;
    const review = props.review || { selectedIds: [], value: null, correct: null };
    const revealCorrect = !!step.showCorrect;
    const isChoice = step.slug === 'single-select' || step.slug === 'true-false' || step.slug === 'multi-select';

    const classify = (answer) => {
        const selected = review.selectedIds && review.selectedIds.includes(answer.qnsAnswerId);
        if (revealCorrect) {
            if (answer.isCorrect) return 'correct';
            if (selected) return 'wrong';
            return 'neutral';
        }
        return selected ? 'selected' : 'neutral';
    };

    const rowStyle = (state) => ({
        display: 'flex',
        alignItems: 'center',
        padding: '10px 12px',
        borderRadius: 8,
        marginBottom: 6,
        backgroundColor: state === 'correct' ? CORRECT_BG : state === 'wrong' ? INCORRECT_BG : state === 'selected' ? SELECTED_BG : 'transparent',
    });

    const labelStyle = (state) => ({
        fontSize: 14,
        color: state === 'correct' ? CORRECT : state === 'wrong' ? INCORRECT : '#3c3c43',
        fontWeight: state === 'neutral' ? 400 : 600,
    });

    const rowIcon = (state) => {
        if (state === 'correct' || state === 'selected') {
            return <Icon iconName="CheckMark" style={{ color: state === 'correct' ? CORRECT : '#3c3c43', fontSize: 16 }} />;
        }
        if (state === 'wrong') {
            return <Icon iconName="Cancel" style={{ color: INCORRECT, fontSize: 16 }} />;
        }
        return null;
    };

    const ratingIcon = ratingTypeOptions.filter(x => x.key === step.ratingType).map(x => x.icon)[0];
    const ratingUnselectedIcon = ratingTypeOptions.filter(x => x.key === step.ratingType).map(x => x.unselectedIcon)[0];

    return (
        <div className="flexcolumn displayflex flex1 prelative quiz-container">
            <div style={{ fontSize: 16 }}>
                {htmr(step.question)}
            </div>

            {revealCorrect && review.correct != null &&
                <div className={styles.pillRow}>
                    <div className={styles.pill} style={{
                        backgroundColor: review.correct ? CORRECT_BG : INCORRECT_BG,
                        color: review.correct ? CORRECT : INCORRECT,
                    }}>
                        <Icon iconName={review.correct ? 'CheckMark' : 'Cancel'} style={{ fontSize: 13, marginRight: 6 }} />
                        {review.correct ? 'Correct!' : 'Incorrect!'}
                    </div>
                </div>
            }

            <div className="displayflex flex1 width100 flexcolumn" style={{ maxWidth: 600, marginTop: 20 }}>
                {isChoice && step.answers && step.answers.map((answer) => {
                    const state = classify(answer);
                    return (
                        <div key={answer.qnsAnswerId} style={rowStyle(state)}>
                            <span style={{ width: 26, display: 'inline-flex' }}>{rowIcon(state)}</span>
                            <span style={labelStyle(state)}>{answer.label}</span>
                        </div>
                    );
                })}

                {step.slug === 'free-answer' &&
                    <div className={styles.freeBox}>
                        {review.value}
                    </div>
                }

                {step.slug === 'rating' &&
                    <div className="displayflex justifycenter width100">
                        <Rating
                            rating={review.value ? Number(review.value) : 0}
                            max={step.ratingSize}
                            readOnly={true}
                            icon={ratingIcon}
                            unselectedIcon={ratingUnselectedIcon}
                        />
                    </div>
                }

                {step.showExplanation && step.explanation &&
                    <div className={styles.explanation}>
                        <h5 className="head-xs mb10" style={{ letterSpacing: 0, color: 'var(--palette-black-alpha-100)', fontSize: 12 }}>
                            {revealCorrect && review.correct === false ? 'WHY IT WAS WRONG' : 'EXPLANATION'}
                        </h5>
                        {htmr(step.explanation)}
                    </div>
                }
            </div>
        </div>
    );
}

const styles = mergeStyleSets({
    pillRow: {
        display: 'flex',
        marginTop: 14,
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '5px 12px',
        borderRadius: 20,
        fontSize: 13,
        fontWeight: 600,
    },
    freeBox: {
        border: '1px solid rgba(0, 0, 0, 0.21)',
        borderRadius: 4,
        padding: '10px 12px',
        minHeight: 70,
        fontSize: 14,
        whiteSpace: 'pre-wrap',
        backgroundColor: 'rgba(var(--palette-neutral-100), 0.02)',
    },
    explanation: {
        marginTop: 20,
        padding: '14px 16px',
        borderRadius: 8,
        backgroundColor: 'rgba(var(--palette-neutral-100), 0.04)',
    },
});

export default ReviewStep;
